/**
 * Game Lab constants, frozen with Foundation (docs/design/add-gamelab.md "Foundation
 * additions" and section 3). The gamelab module owns the rest of this folder.
 */
import { SPOT_KEYS as CORE_SPOT_KEYS, type SpotKey } from '../../core/spots';

/** The 12 court spots (ShotSpotDiagram plus the two layup spots l and r). */
export const SPOT_KEYS = CORE_SPOT_KEYS;
export type { SpotKey };

/** spotValue(s): arc 3, lb le re rb 2, ft l r 1. */
export const SPOT_VALUE: Record<SpotKey, 1 | 2 | 3> = {
  lc: 3,
  lw: 3,
  top: 3,
  rw: 3,
  rc: 3,
  lb: 2,
  le: 2,
  re: 2,
  rb: 2,
  ft: 1,
  l: 1,
  r: 1,
};

export const LAB_ICON_KEYS = [
  'letters',
  'globe',
  'clock',
  'dice',
  'crown',
  'ladder',
  'skull',
  'relay',
  'copy',
  'shuffle',
  'target',
  'fire',
  'ball',
  'whistle',
  'trophy',
  'star',
] as const;
export type LabIconKey = (typeof LAB_ICON_KEYS)[number];

export const LAB_EQUIPMENT = ['ball', 'second_ball', 'cones', 'chairs', 'stopwatch', 'chalk'] as const;
export type LabEquipment = (typeof LAB_EQUIPMENT)[number];

export const LAB_TAGS = [
  'classic',
  'shooting',
  'spots',
  'elimination',
  'teams',
  'timed',
  'chaos',
  'duel',
  'warmup',
  'party',
] as const;
export type LabTag = (typeof LAB_TAGS)[number];

export const LAB_SOUNDS = ['swish', 'brick', 'buzzer', 'cheer', 'roll'] as const;
export type LabSound = (typeof LAB_SOUNDS)[number];

export const DSL_VERSION = 1;

export const DSL_LIMITS = {
  bytes: 65536,
  vars: 40,
  actions: 16,
  phases: 8,
  nodes: 40,
  exprChars: 200,
  exprDepth: 12,
  opsPerEvent: 5000,
  gotosPerEvent: 8,
  triggerPasses: 8,
  eventsPerRun: 5000,
  players: 16,
  timerMaxS: 600,
} as const;

/** Fun XP (A6): +10 per counted run, +5 per winner, +5 for a new slug, 60 a day cap. */
export const FUN_XP = { run: 10, win: 5, newSlug: 5, dailyCap: 60 } as const;

/** A run counts for Fun XP when final, with >= 2 players, >= 3 minutes and >= 10 actions. */
export const FUN_XP_RUN_RULES = { minPlayers: 2, minMinutes: 3, minActions: 10 } as const;

/** No scope can shelve, start runs or read members, devices, media, studio, invites or settings (A7). */
export const AGENT_SCOPES = ['lab:read', 'lab:write', 'lab:submit'] as const;
export type AgentScope = (typeof AGENT_SCOPES)[number];

export const LAB_BADGES = ['lab_rat', 'mad_scientist'] as const;
export type LabBadge = (typeof LAB_BADGES)[number];

export const GAME_DEF_STATUSES = ['draft', 'ready', 'shelf', 'superseded', 'retired', 'discarded'] as const;
export type GameDefStatus = (typeof GAME_DEF_STATUSES)[number];
export const GAME_DEF_ORIGINS = ['builtin', 'agent'] as const;

/** ADDENDUM A8: the agent sees aliases (P1..Pn) unless the owner flips this to 'names'. */
export const SHARE_NAMES_DEFAULT = 'aliases' as const;

/** Per-connection agent limits (section 2.2). */
export const AGENT_LIMITS = {
  callsPerMinute: 60,
  callsPerDay: 1000,
  simulatePerMinute: 20,
  simulateOps: 1_000_000,
  writesPerDay: 30,
  openDrafts: 20,
  submitsPerDay: 5,
  repliesPerDay: 50,
  allConnectionsPerMinute: 300,
} as const;

export const MCP_BODY_LIMIT = 256 * 1024;
export const OAUTH_BODY_LIMIT = 8 * 1024;
export const AGENT_TOKEN_PREFIX = 'ttsa_';
